import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { Option } from '../../types/database'
import { formatOptionValue } from '../../utils/formatters'
import { useAuth } from '../../contexts/AuthContext'
import { useProfile } from '../../hooks/useProfile'
import { invalidateAircraftOptionsCache } from '../../hooks/useAircraftOptions'

interface AircraftOptionsProps {
  aircraftId: number
  modelId: number
  readonly?: boolean
}

export default function AircraftOptions({ aircraftId, modelId, readonly = false }: AircraftOptionsProps) {
  const { user } = useAuth()
  const { profile } = useProfile()
  const [options, setOptions] = useState<Option[]>([])
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [hasEditAccess, setHasEditAccess] = useState(false)
  const [aircraftExists, setAircraftExists] = useState(false)

  useEffect(() => {
    if (modelId) {
      fetchOptions()
      fetchAircraftOptions()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, aircraftId])

  // Separate effect for checking edit access when profile loads
  useEffect(() => {
    if (profile !== undefined) {
      checkEditAccess()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, modelId, user])

  const checkEditAccess = async () => {
    // Since we want all authenticated users to have access, just check if user is logged in
    if (user && profile) {
      setHasEditAccess(true)
      return
    }
    
    setHasEditAccess(false)
  }

  const fetchOptions = async () => {
    try {
      const { data, error } = await supabase
        .from('options')
        .select('*')
        .eq('model_id', modelId)
        .order('name')

      if (error) throw error
      setOptions(data || [])
    } catch (err) {
      // Error fetching options
    }
  }

  const fetchAircraftOptions = async () => {
    try {
      const { data, error } = await supabase
        .from('aircraft')
        .select('*')
        .eq('aircraft_id', aircraftId)
        .maybeSingle()
      
      if (error && error.code !== 'PGRST116') {
        throw error
      }
      
      if (data) {
        setAircraftExists(true)
        setSelectedOptions(data.options || [])
      } else {
        // Aircraft doesn't exist in our database yet
        setAircraftExists(false)
        setSelectedOptions([])
      }
    } catch (err) {
      // Error fetching aircraft options
    } finally {
      setLoading(false)
    }
  }

  const handleToggleOption = (optionId: string) => {
    if (readonly || !hasEditAccess) return

    setSelectedOptions(prev => {
      if (prev.includes(optionId)) {
        return prev.filter(id => id !== optionId)
      } else {
        return [...prev, optionId]
      }
    })
  }

  const handleSave = async () => {
    try {
      setSaving(true)

      if (aircraftExists) {
        // Update existing aircraft
        const { error } = await supabase
          .from('aircraft')
          .update({ options: selectedOptions })
          .eq('aircraft_id', aircraftId)

        if (error) throw error
      } else {
        // Insert new aircraft
        const { error } = await supabase
          .from('aircraft')
          .insert({
            aircraft_id: aircraftId,
            options: selectedOptions
          })

        if (error) throw error
        setAircraftExists(true)
      }

      // Invalidate the options cache for this aircraft to ensure other components refresh
      invalidateAircraftOptionsCache([aircraftId])

      // Show success message (you could use a toast library here)
      alert('Options saved successfully!')
    } catch (err: any) {
      alert('Failed to save options: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const calculateTotalValue = () => {
    return selectedOptions.reduce((total, optionId) => {
      const option = options.find(o => o.option_id === optionId)
      return total + (option?.value || 0)
    }, 0)
  }

  if (loading) {
    return (
      <div className="bg-gray-50 rounded-xl shadow-lg p-6 border-2 border-gray-300">
        <div className="text-gray-600">Loading options...</div>
      </div>
    )
  }

  if (options.length === 0) {
    return (
      <div className="bg-gray-50 rounded-xl shadow-lg overflow-hidden border-2 border-gray-300">
        <div className="bg-gray-800 px-6 py-4">
          <h3 className="text-xl font-bold text-gray-200 flex items-center">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Optional Equipment Configuration
          </h3>
        </div>
        <div className="p-6 bg-gray-100">
          <p className="text-sm text-gray-600 italic">
            No optional equipment available for this model.
          </p>
        </div>
      </div>
    )
  }

  const canEdit = hasEditAccess && !readonly

  return (
    <div className="bg-gray-50 rounded-xl shadow-lg overflow-hidden border-2 border-gray-300">
      <div className="bg-gray-800 px-6 py-4 flex justify-between items-center">
        <h3 className="text-xl font-bold text-gray-200 flex items-center">
          <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Optional Equipment Configuration
        </h3>
        {canEdit && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-gray-200 text-gray-800 hover:bg-gray-300 px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          >
            {saving ? 'Saving...' : 'Save Options'}
          </button>
        )}
      </div>
      <div className="p-6 bg-gray-100">
        <div className="space-y-3">
          {options.map((option) => {
            const isSelected = selectedOptions.includes(option.option_id)
            return (
              <label
                key={option.option_id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  isSelected 
                    ? 'border-gray-600 bg-gray-200' 
                    : 'border-gray-300 bg-white hover:bg-gray-50'
                } ${canEdit ? 'cursor-pointer' : 'cursor-default'} transition-colors duration-150`}
              >
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleOption(option.option_id)}
                    disabled={!canEdit}
                    className="h-4 w-4 text-gray-700 focus:ring-gray-500 border-gray-400 rounded disabled:opacity-50"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{option.name}</div>
                    <div className="text-xs text-gray-500">{formatOptionValue(option.value)}</div>
                  </div>
                </div>
              </label>
            )
          })}
        </div>
        
        {selectedOptions.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-300">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">
                Total Options Value:
              </span>
              <span className="text-sm font-bold text-gray-900">
                {formatOptionValue(calculateTotalValue())}
              </span>
            </div>
          </div>
        )}

        {!canEdit && (
          <div className="mt-4 text-xs text-gray-500 italic">
            {!user ? 'Please log in to manage options.' : 'Loading user permissions...'}
          </div>
        )}
      </div>
    </div>
  )
}